import { useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

export default function ProjectDesignCatalog() {
  const [index, setIndex] = useState(0);

  const sections = [
    {
      title: "Climate-Smart Farms",
      desc: "Food systems designed to restore soil, increase yields, and power resilient local economies.",
      img: "https://images.unsplash.com/photo-1592982537447-7440770cbfc9?auto=format&fit=crop&w=1800&q=80",
    },
    {
      title: "Agroforestry Plots",
      desc: "Integrated ecosystems combining trees, crops, and livelihoods for long-term sustainability.",
      img: "https://theforefrontmagazine.com/wp-content/uploads/2024/03/Agro.jpg",
    },
    {
      title: "Farmer Field Schools",
      desc: "Hands-on learning environments where farmers gain practical training in climate-smart agriculture, maize cultivation, and sustainable farming practices.",
      img: "https://www.sugeco.or.tz/media/Projects/AIDI-VISIT-DEMO-FARM_40.jpg-40.jpg",
    },
    {
      title: "Youth Innovation Parks",
      desc: "Spaces where young builders create climate startups and prototype green technologies.",
      img: "https://t3.ftcdn.net/jpg/01/62/37/68/360_F_162376805_3SO2RyjjU1WvAyzYZk19y54CxtlYbCgU.jpg",
    },
  ];

  const itemsPerView = 2;
  const maxIndex = Math.ceil(sections.length / itemsPerView) - 1;

  const next = () => {
    setIndex((prev) => Math.min(prev + 1, maxIndex));
  };

  const prev = () => {
    setIndex((prev) => Math.max(prev - 1, 0));
  };

  return (
    <section className="bg-[#F7F4EE] py-12 lg:py-12 overflow-hidden">

      {/* HEADER */}
      <div className="max-w-7xl mx-auto px-6 md:px-1 flex flex-col md:flex-row md:items-end md:justify-between gap-10 mb-20">

        <div>
          <p className="text-xs tracking-[0.45em] text-green-500 uppercase">
            PROJECT PLATFORM
          </p>

          <h2 className="mt-6 text-3xl md:text-6xl font-semibold text-[#1E1B16] leading-tight">
            Design Catalog of
            <span className="block text-green-500">
              Regenerative Systems
            </span>
          </h2>

          <p className="mt-6 text-[#5C5548] text-md lg:text-lg max-w-2xl leading-relaxed">
            Each project design is modular, replicable, and adaptable to local
            contexts, with a strong focus on long-term financial and
            environmental sustainability. The catalog features scalable models
            such as climate-smart farming systems, agroforestry landscapes,
            youth-led innovation spaces, and practical training environments
            that strengthen sustainable livelihoods and climate resilience.
          </p>
        </div>

        {/* DESKTOP NAV */}
        <div className="hidden md:flex gap-4">
          <button
            onClick={prev}
            disabled={index === 0}
            className="w-12 h-12 rounded-full border border-green-200 flex items-center justify-center hover:bg-green-50 transition disabled:opacity-40"
          >
            <ChevronLeft size={22} />
          </button>

          <button
            onClick={next}
            disabled={index === maxIndex}
            className="w-12 h-12 rounded-full border border-green-200 flex items-center justify-center hover:bg-green-50 transition disabled:opacity-40"
          >
            <ChevronRight size={22} />
          </button>
        </div>

      </div>

      {/* SLIDER */}
      <div className="w-full overflow-hidden">
        <div
          className="flex transition-transform duration-700 ease-in-out"
          style={{
            transform: `translateX(-${index * 100}%)`,
          }}
        >
          {sections.map((s, i) => (
            <div
              key={i}
              className="w-full md:w-1/2 flex-shrink-0 px-4 md:px-8"
            >
              <div className="group relative h-[520px] md:h-[600px] rounded-none md:rounded-[30px] overflow-hidden shadow-2xl">

                {/* IMAGE */}
                <img
                  src={s.img}
                  alt=""
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />

                {/* OVERLAY */}
                <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A]/90 via-black/20 to-transparent" />

                {/* subtle border */}
                <div className="absolute inset-0 ring-1 ring-green-400/20 rounded-none md:rounded-[30px]" />

                {/* CONTENT */}
                <div className="absolute bottom-10 left-6 md:left-10 right-6 md:right-10 text-white">

                  <h3 className="text-2xl md:text-4xl font-semibold leading-snug">
                    {s.title}
                  </h3>

                  <p className="mt-4 text-sm text-white/75 max-w-md leading-relaxed">
                    {s.desc}
                  </p>

                  <div className="mt-6 w-12 h-[2px] bg-green-400 group-hover:w-20 transition-all duration-500" />
                </div>

              </div>
            </div>
          ))}
        </div>
      </div>

      {/* MOBILE NAV */}
      <div className="flex md:hidden justify-end gap-4 mt-8 px-6">
        <button
          onClick={prev}
          disabled={index === 0}
          className="w-12 h-12 rounded-full border border-green-200 flex items-center justify-center hover:bg-green-50 transition disabled:opacity-40"
        >
          <ChevronLeft size={22} />
        </button>

        <button
          onClick={next}
          disabled={index === maxIndex}
          className="w-12 h-12 rounded-full border border-green-200 flex items-center justify-center hover:bg-green-50 transition disabled:opacity-40"
        >
          <ChevronRight size={22} />
        </button>
      </div>

    </section>
  );
}